import * as z from 'zod';
import {
  discountTypeSchema,
  merchantModerationStateSchema,
  optional,
  promoTypeSchema,
  timestampSchema,
} from './common';
import {
  localizedTextSchema,
  pendingChangeRequestSchema,
  productSchema,
} from './product';
import { restaurantSchema } from './restaurant';

// Promotion Service — `v1Promotion` plus `v1MerchantPromotionItem`, the joined
// row the merchant promotions table renders (product × restaurant × promotion).

export const promotionSchema = z.object({
  id: z.string(),
  restaurant_id: z.string().default(''),
  name: z.string().default(''),
  promo_type: promoTypeSchema.optional(),
  discount_type: discountTypeSchema.optional(),
  /** Percentage points or fixed so'm, per `discount_type`. */
  discount_value: z.number().int().nonnegative().default(0),
  buy_quantity: z.number().int().nonnegative().default(0),
  get_quantity: z.number().int().nonnegative().default(0),
  is_active: z.boolean().default(false),
  /** Promo window; both absent means open-ended. */
  starts_at: optional(timestampSchema),
  ends_at: optional(timestampSchema),
  image: z.string().default(''),
  created_at: optional(timestampSchema),
  updated_at: optional(timestampSchema),
  product_ids: z.array(z.string()).default([]),
});
export type Promotion = z.infer<typeof promotionSchema>;

/**
 * `v1LocalizedPromotion` — the same field set as `Promotion`, but with `name`
 * exposed as EVERY language at once. The write RPCs (create/batch/update) and
 * the merchant read (`promotions/{id}/by-merchant`) answer with this shape;
 * the list reads still answer with the locale-resolved `Promotion`.
 */
export const localizedPromotionSchema = promotionSchema.extend({
  name: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
});
export type LocalizedPromotion = z.infer<typeof localizedPromotionSchema>;

/**
 * Single-resource payload — `data` on the merchant read
 * (`v1GetMerchantPromotionResponse`). `promotion` is required: reads are never
 * gated.
 */
export const localizedPromotionResponseSchema = z.object({
  promotion: localizedPromotionSchema,
});

/**
 * Single-resource payload for published promotion read (`GET /promotions/{id}/published`).
 * Returned as `{ client_visible: true, promotion }` or `{ client_visible: false, promotion: null }`.
 */
export const publishedPromotionCardResponseSchema = z.object({
  client_visible: z.boolean().default(true),
  promotion: localizedPromotionSchema.nullable().optional(),
});
export type PublishedPromotionCardResponse = z.infer<
  typeof publishedPromotionCardResponseSchema
>;

/**
 * `data` on create/batch/update (`v1CreatePromotionResponse` /
 * `v1UpdatePromotionResponse`).
 *
 * `promotion` is OPTIONAL: a gated write answers with only a `pending_change`
 * receipt (verified live: `{result:'pending_change', pending_change:{…}}`), and
 * even the applied branch answers with the LOCALIZED shape — parsing either
 * against the old resolved-`name` schema threw on a request that succeeded.
 */
export const promotionWriteResponseSchema = z.object({
  promotion: localizedPromotionSchema.optional(),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type PromotionWriteResult = z.infer<typeof promotionWriteResponseSchema>;

/**
 * `data` on `DELETE /promotions/{id}` (`v1DeletePromotionResponse`) — no
 * entity, only the optional moderation receipt: a gated delete is QUEUED, and
 * the row survives until a moderator approves it.
 */
export const deletePromotionResponseSchema = z.object({
  pending_change: pendingChangeRequestSchema.optional(),
});
/** Shared by the campaign DELETE and the per-product unlink — same receipt. */
export type DeletePromotionResult = z.infer<typeof deletePromotionResponseSchema>;

/**
 * `v1MerchantPromotionItem` — one row of `GET /promotions/by-merchant`: the
 * discounted product, the branch it belongs to, and the promotion itself.
 *
 * `moderation_state` is the row's review verdict (`UNSPECIFIED` = no verdict);
 * `rejection_reason` carries the moderator's note on a rejection.
 */
export const merchantPromotionItemSchema = z.object({
  product: productSchema,
  restaurant: restaurantSchema,
  promotion: promotionSchema,
  moderation_state: merchantModerationStateSchema
    .optional()
    .default('MERCHANT_MODERATION_STATE_UNSPECIFIED'),
  rejection_reason: z.string().default(''),
});
export type MerchantPromotionItem = z.infer<typeof merchantPromotionItemSchema>;
